/**
 * ONE label scale for every input inside a DETAIL POPUP (programme item,
 * speaker, sponsor) — guest parity: a `body-3-medium` label six pixels above
 * its control, which is what `DataTypeInput` (and therefore every CUSTOM field)
 * has always rendered.
 *
 * Without this, one popup showed four label scales at once: `FormInput` and
 * `MultilineInput` default to `body-2-medium`, `OptionInput` used to hardcode
 * it, the programme item's schedule hand-rolls its own `<p>`, and custom fields
 * came in at `body-3-medium` — so "Email" and a custom field sat in the same
 * grid row at different sizes.
 *
 * ⚠ NEVER "fix" this by changing `FormInput`/`MultilineInput`'s own defaults:
 * ~370 call sites across ~190 files ride `body-2-medium`, and that is an
 * app-wide typography change, not a popup one.
 */

/** Spread into `FormInput` / `MultilineInput` (they share the prop names). */
export const DETAIL_LABEL = {
  labelFontSize: "body-3-medium",
  margin: "mt-1.5",
} as const;

/** `OptionInput` puts its gap BELOW the label, hence a different prop. */
export const DETAIL_LABEL_OPTION = {
  labelFontSize: "body-3-medium",
  labelMargin: "mb-1.5",
} as const;

/**
 * A DISPLAY-ONLY value, dressed as the input it replaces.
 *
 * Same pill as `FormInput` (`border outline-quaternary surface-primary`,
 * `rounded-[10px]`, `h-9`) so a read-only field sits in a popup grid at the same
 * height and weight as its editable neighbours — a bare line of text next to
 * boxed inputs read as a rendering bug, not as "you cannot change this".
 *
 * `opacity-60` is the app's existing "locked" signal (DataTypeInput uses exactly
 * this for a conditionally locked field), which is what makes it read as
 * deliberately non-editable rather than merely pale. Pair it with `inert` +
 * `aria-hidden` on the same element: the visuals say disabled, `inert` makes it
 * true — no focus, no tab stop, no selection.
 */
export const DETAIL_READ_ONLY_BOX =
  "flex items-center h-9 px-2 rounded-[10px] border outline-quaternary surface-primary opacity-60 select-none";

/** One scale and one colour for every read-only value, whatever its dataType.
 *  The TCC read branches disagree among themselves (see `readTextClass`). */
export const DETAIL_READ_ONLY_TEXT = "body-2-regular font-primary truncate";
